import { createSignal, onMount, For, Show, createMemo } from "solid-js";
import { useLocation } from '@solidjs/router';
import { useToast } from '../components/MessageBox';
import { useApi, isOk } from '../api';
import Text from '../components/Text';
import { useTranslation } from '../hooks/useTranslation';
import { Client, OAuthClientType } from '../models/OAuthClient';
import { GrantedRedirectUri } from "../models/GrantedRedirectUri";
import { formatDateTime } from "../services/dateFormat";

export default function OAuthClientPage() {

    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();

    const location = useLocation();
    const clientId = createMemo(() => {
        const pathParts = location.pathname.split('/');
        return pathParts[pathParts.length - 1];
    });

    const [newRedirectUri, setNewRedirectUri] = createSignal("");
    const [client, setClient] = createSignal<Client>({
        id: "",
        clientName: "",
        createdAt: ""
    });
    const [redirectUris, setRedirectUris] = createSignal<GrantedRedirectUri[]>([]);

    onMount(fetchClientDetails);

    function fetchClientDetails() {
        api.oauth.getClient(clientId())
            .then(data => {
                if (isOk(data.data)) {
                    setClient(data.data.value as unknown as Client);
                    fetchRedirectUris(clientId());
                }
            });
    }

    function fetchRedirectUris(clientId: string) {
        api.oauth.getRedirectUris(clientId)
            .then(data => {
                if (isOk(data.data)) {
                    setRedirectUris((data.data.value?.redirectUris ?? []) as unknown as GrantedRedirectUri[]);
                }
            });
    }

    async function updateClient(e: Event) {
        e.preventDefault();
        const data = await api.oauth.updateClient(client() as never);
        if (isOk(data.data)) {
            toast?.info(t("OAUTH_CLIENT_UPDATED"));
        } else {
            toast?.error(t("OAUTH_CLIENT_UPDATE_ERROR"));
        }
    }

    function regenerateClientSecret() {
        const cid = client().id;
        api.oauth.regenerateClientSecret(cid)
            .then(data => {
                if (data.status == 200 && isOk(data.data)) {
                    setClient({ ...client(), clientSecret: data.data.value?.clientSecret });
                    toast?.info(t("OAUTH_SECRET_REGENERATED"));
                } else {
                    toast?.error(t("OAUTH_SECRET_REGENERATE_ERROR"));
                }
            });
    }

    function deleteRedirectUri(id: string) {
        api.oauth.removeRedirectUri(client().id, id)
            .then(data => {
                if (data.status == 200) {
                    setRedirectUris(redirectUris().filter(uri => uri.id !== id));
                    toast?.info(t("OAUTH_REDIRECT_DELETED"));
                } else {
                    toast?.error(t("OAUTH_REDIRECT_DELETE_ERROR"));
                }
            });
    }

    async function addRedirectUri(e: Event){
        e.preventDefault();
        const uriInput = newRedirectUri();
        if (!uriInput) {
            toast?.error(t("OAUTH_REDIRECT_ENTER_PROMPT"));
            return;
        }
        const data = await api.oauth.addRedirectUri(client().id, { uri: uriInput });
        if (isOk(data.data)) {
            setRedirectUris([...redirectUris(), data.data.value as unknown as GrantedRedirectUri]);
            setNewRedirectUri("");
            fetchClientDetails();
            toast?.info(t("OAUTH_REDIRECT_ADDED"));
        } else {
            toast?.error(t("OAUTH_REDIRECT_ADD_ERROR"));
        }
    }

    return (
        <div class="dashboard-page">
            <h1><Text val="OAUTH_CLIENT_DETAILS" /></h1>

            <div class="page-card">
                <div class="page-section">
                    <div class="field-group">
                        <span class="field-label"><Text val="ID" /></span>
                        <input disabled type="text" value={client().id} />
                    </div>

                    <div class="field-group">
                        <span class="field-label"><Text val="CLIENT_NAME" /></span>
                        <form onSubmit={updateClient} class="form-row">
                            <input
                                type="text"
                                value={client().clientName}
                                onInput={(e) => setClient({...client(), clientName: (e.target as HTMLInputElement).value})}
                            />
                            <select
                                value={client().clientType ?? OAuthClientType.Public}
                                onChange={(e) => setClient({...client(), clientType: parseInt((e.target as HTMLSelectElement).value) as OAuthClientType})}
                            >
                                <option value={OAuthClientType.Public}>{t("OAUTH_CLIENT_TYPE_PUBLIC")}</option>
                                <option value={OAuthClientType.Confidential}>{t("OAUTH_CLIENT_TYPE_CONFIDENTIAL")}</option>
                            </select>
                            <button type="submit" class="btn btn-primary btn-sm" aria-label={t("SAVE")}><i class="fa-solid fa-floppy-disk" aria-hidden="true"></i></button>
                        </form>
                    </div>

                    <div class="field-group">
                        <span class="field-label"><Text val="CLIENT_ID" /></span>
                        <input disabled type="text" value={client().clientId} />
                    </div>

                    <div class="field-group">
                        <span class="field-label"><Text val="CLIENT_SECRET" /></span>
                        <input disabled type="text" value={client().clientSecret == "" ? t("OAUTH_SECRET_HIDDEN") : client().clientSecret} />
                        <div class="form-row">
                            <Show when={client().clientSecret}>
                                <button class="btn btn-secondary btn-sm" onClick={() => {
                                    navigator.clipboard.writeText(client().clientSecret || "");
                                    toast?.info(t("OAUTH_SECRET_COPIED"));
                                }}><i class="fa-solid fa-copy"></i> <Text val="COPY_CLIENT_SECRET" /></button>
                            </Show>
                            <button class="btn btn-warning btn-sm" onClick={regenerateClientSecret}><i class="fa-solid fa-rotate"></i> <Text val="REGENERATE_CLIENT_SECRET" /></button>
                        </div>
                        <Show when={client().clientSecret}>
                            <p style={{ color: 'var(--warning-500)', "font-size": 'var(--font-size-sm)', margin: '0' }}><Text val="KEEP_SECRET_SAFE" /></p>
                        </Show>
                    </div>

                    <div class="field-group">
                        <span class="field-label"><Text val="CREATEDAT" /></span>
                        <span class="field-value">{formatDateTime(client().createdAt)}</span>
                    </div>
                </div>
            </div>

            <div class="page-card">
                <div class="page-section">
                    <h2><Text val="REDIRECT_URIS" /></h2>
                    <form onSubmit={addRedirectUri} class="form-row">
                        <input
                            type="text"
                            placeholder={t("OAUTH_NEW_REDIRECT_URI")}
                            value={newRedirectUri()}
                            onInput={(e) => setNewRedirectUri((e.target as HTMLInputElement).value)}
                        />
                        <button type="submit" class="btn btn-primary btn-sm" aria-label={t("OAUTH_ADD_REDIRECT_URI")}><i class="fa-solid fa-plus" aria-hidden="true"></i></button>
                    </form>
                    <table class='sessiontable'>
                        <thead>
                            <tr>
                                <th><Text val="URI" /></th>
                                <th><Text val="USES" /></th>
                                <th><i class="fa-solid fa-trash"></i></th>
                            </tr>
                        </thead>
                        <tbody>
                            <For each={redirectUris()}>
                                {(uri) => (
                                    <tr>
                                        <td>{uri.redirectUri}</td>
                                        <td>{uri.uses}</td>
                                        <td><button class='danger' onClick={() => deleteRedirectUri(uri.id)}><i class="fa-solid fa-trash"></i></button></td>
                                    </tr>
                                )}
                            </For>
                        </tbody>
                    </table>
                </div>
            </div>

            <div class="page-card">
                <div class="page-section">
                    <h2><Text val="EXAMPLE_OAUTH_START_URL" /></h2>
                    <button class="btn btn-secondary" onClick={() => {
                        const isPublic = (client().clientType ?? OAuthClientType.Public) === OAuthClientType.Public;
                        let example = "https://panel.dsm-clan.de/api/oauth/authorize?client_id=" + client().clientId
                            + "&redirect_uri=" + encodeURIComponent(redirectUris()[0]?.redirectUri || "https://example.com/oauth")
                            + "&response_type=code&scope=read"
                            + (isPublic ? "&code_challenge=YOUR_S256_CODE_CHALLENGE&code_challenge_method=S256" : "");
                        navigator.clipboard.writeText(example);
                        toast?.info(t("OAUTH_EXAMPLE_URL_COPIED"));
                    }}><i class="fa-solid fa-copy"></i> <Text val="COPY_EXAMPLE_OAUTH_START" /></button>
                </div>
            </div>

            <div class="oauth-tutorial">
                <div class="oauth-tutorial-header">
                    <h2 class="oauth-tutorial-title"><Text val="OAUTH_API_TUTORIAL" /></h2>
                    <p class="oauth-tutorial-subtitle">
                        <Text val="OAUTH_TUTORIAL_THIS_IS_A" /> <strong>{(client().clientType ?? OAuthClientType.Public) === OAuthClientType.Confidential ? t("OAUTH_CLIENT_TYPE_CONFIDENTIAL") : t("OAUTH_CLIENT_TYPE_PUBLIC")}</strong> <Text val="OAUTH_TUTORIAL_CLIENT_WORD" />
                        {" "}{(client().clientType ?? OAuthClientType.Public) === OAuthClientType.Confidential
                            ? t("OAUTH_TUTORIAL_CONFIDENTIAL_DESC")
                            : t("OAUTH_TUTORIAL_PUBLIC_DESC")}
                    </p>
                </div>

                <div class="endpoint-showcase">
                    <div class="endpoint-header">
                        <span class="method-badge">POST</span>
                        <span class="endpoint-url">/api/oauth/token</span>
                    </div>
                    <div style={{ padding: 'var(--spacing-xl)' }}>
                        <h3 style={{ color: 'var(--dark-text-primary)', "margin-bottom": 'var(--spacing-xl)' }}><Text val="TOKEN_ENDPOINT" /></h3>

                        <div class="auth-methods-grid">
                            <Show when={(client().clientType ?? OAuthClientType.Public) === OAuthClientType.Public}>
                                <div class="auth-method-card">
                                    <h4 class="auth-method-title">
                                        <i class="fa-solid fa-shield-alt"></i>
                                        <Text val="OAUTH_METHOD_PKCE_TITLE" />
                                    </h4>
                                    <p class="auth-method-description">
                                        <Text val="OAUTH_METHOD_PKCE_DESC_1" /> <code>code_verifier</code> <Text val="OAUTH_METHOD_PKCE_DESC_2" /> <code>code_challenge</code><Text val="OAUTH_METHOD_PKCE_DESC_3" />
                                    </p>
                                    <div class="code-example">
{`curl -X POST https://panel.dsm-clan.de/api/oauth/token \\
  -H "Content-Type: application/x-www-form-urlencoded" \\
  -d "grant_type=authorization_code" \\
  -d "client_id=${client().clientId || 'your_client_id'}" \\
  -d "code=authorization_code_from_callback" \\
  -d "redirect_uri=${redirectUris()[0]?.redirectUri || 'your_redirect_uri'}" \\
  -d "code_verifier=your_pkce_code_verifier"`}
                                    </div>
                                    <button class="copy-code-btn" onClick={() => {
                                        const curlCommand = `curl -X POST https://panel.dsm-clan.de/api/oauth/token \\\n  -H "Content-Type: application/x-www-form-urlencoded" \\\n  -d "grant_type=authorization_code" \\\n  -d "client_id=${client().clientId || 'your_client_id'}" \\\n  -d "code=authorization_code_from_callback" \\\n  -d "redirect_uri=${redirectUris()[0]?.redirectUri || 'your_redirect_uri'}" \\\n  -d "code_verifier=your_pkce_code_verifier"`;
                                        navigator.clipboard.writeText(curlCommand);
                                        toast?.info(t("OAUTH_CURL_COPIED"));
                                    }}>
                                        <i class="fa-solid fa-copy"></i>
                                        <Text val="COPY_CURL" />
                                    </button>
                                </div>
                            </Show>

                            <Show when={(client().clientType ?? OAuthClientType.Public) === OAuthClientType.Confidential}>
                                <div class="auth-method-card">
                                    <h4 class="auth-method-title">
                                        <i class="fa-solid fa-key"></i>
                                        <Text val="OAUTH_METHOD_SECRET_TITLE" />
                                    </h4>
                                    <p class="auth-method-description">
                                        <Text val="OAUTH_METHOD_SECRET_DESC_1" /> <code>client_secret</code> <Text val="OAUTH_METHOD_SECRET_DESC_2" />
                                    </p>
                                    <div class="code-example">
{`curl -X POST https://panel.dsm-clan.de/api/oauth/token \\
  -H "Content-Type: application/x-www-form-urlencoded" \\
  -d "grant_type=authorization_code" \\
  -d "client_id=${client().clientId || 'your_client_id'}" \\
  -d "client_secret=your_client_secret" \\
  -d "code=authorization_code_from_callback" \\
  -d "redirect_uri=${redirectUris()[0]?.redirectUri || 'your_redirect_uri'}"`}
                                    </div>
                                    <button class="copy-code-btn" onClick={() => {
                                        const curlCommand = `curl -X POST https://panel.dsm-clan.de/api/oauth/token \\\n  -H "Content-Type: application/x-www-form-urlencoded" \\\n  -d "grant_type=authorization_code" \\\n  -d "client_id=${client().clientId || 'your_client_id'}" \\\n  -d "client_secret=your_client_secret" \\\n  -d "code=authorization_code_from_callback" \\\n  -d "redirect_uri=${redirectUris()[0]?.redirectUri || 'your_redirect_uri'}"`;
                                        navigator.clipboard.writeText(curlCommand);
                                        toast?.info(t("OAUTH_CURL_COPIED"));
                                    }}>
                                        <i class="fa-solid fa-copy"></i>
                                        <Text val="COPY_CURL" />
                                    </button>
                                </div>
                            </Show>

                            <div class="auth-method-card">
                                <h4 class="auth-method-title">
                                    <i class="fa-solid fa-refresh"></i>
                                    <Text val="OAUTH_METHOD_REFRESH_TITLE" />
                                </h4>
                                <p class="auth-method-description">
                                    <Text val="OAUTH_METHOD_REFRESH_DESC" />
                                </p>
                                <div class="code-example">
{`curl -X POST https://panel.dsm-clan.de/api/oauth/token \\
  -H "Content-Type: application/x-www-form-urlencoded" \\
  -d "grant_type=refresh_token" \\
  -d "client_id=${client().clientId || 'your_client_id'}" \\
  -d "refresh_token=your_refresh_token"`}
                                </div>
                                <button class="copy-code-btn" onClick={() => {
                                    const curlCommand = `curl -X POST https://panel.dsm-clan.de/api/oauth/token \\\n  -H "Content-Type: application/x-www-form-urlencoded" \\\n  -d "grant_type=refresh_token" \\\n  -d "client_id=${client().clientId || 'your_client_id'}" \\\n  -d "refresh_token=your_refresh_token"`;
                                    navigator.clipboard.writeText(curlCommand);
                                    toast?.info(t("OAUTH_CURL_COPIED"));
                                }}>
                                    <i class="fa-solid fa-refresh"></i>
                                    <Text val="COPY_CURL" />
                                </button>
                            </div>
                        </div>

                        <div class="response-section">
                            <h4 class="response-title">
                                <i class="fa-solid fa-check-circle"></i>
                                <Text val="EXPECTED_RESPONSE" />
                            </h4>
                            <div class="code-example">
{`{
  "access_token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",
  "token_type": "Bearer",
  "expires_in": 600,
  "refresh_token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",
  "scope": "basic"
}`}
                            </div>

                            <div class="token-info-grid">
                                <div class="token-info-card">
                                    <div class="token-type-title"><Text val="ACCESS_TOKEN" /></div>
                                    <div class="token-type-description">
                                        <Text val="ACCESS_TOKEN_DESC" />
                                    </div>
                                </div>
                                <div class="token-info-card">
                                    <div class="token-type-title"><Text val="REFRESH_TOKEN" /></div>
                                    <div class="token-type-description">
                                        <Text val="REFRESH_TOKEN_DESC" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="security-warning">
                            <i class="fa-solid fa-exclamation-triangle security-warning-icon"></i>
                            <div class="security-warning-text">
                                <Show when={(client().clientType ?? OAuthClientType.Public) === OAuthClientType.Confidential}>
                                    <strong><Text val="OAUTH_WARNING_IMPORTANT" /></strong> <Text val="OAUTH_WARNING_SECRET_1" /> <code>client_secret</code> <Text val="OAUTH_WARNING_SECRET_2" />
                                </Show>
                                <Show when={(client().clientType ?? OAuthClientType.Public) === OAuthClientType.Public}>
                                    <strong><Text val="OAUTH_WARNING_IMPORTANT" /></strong> <Text val="OAUTH_WARNING_PKCE_1" /> <code>code_challenge</code> <Text val="OAUTH_WARNING_PKCE_2" /> <code>code_challenge_method=S256</code> <Text val="OAUTH_WARNING_PKCE_3" />
                                </Show>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
